import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { getAsset, type AssetId, type AssetManifestEntry } from "../content/assetManifest";

interface AssetImageProps {
  assetId: AssetId;
  className?: string;
  sizes?: string;
  alt?: string;
  eager?: boolean;
  draggable?: boolean;
}

function sourceSet(asset: AssetManifestEntry, format: "avif" | "webp"): string | undefined {
  const sources = asset.sources[format];
  return sources?.map((source) => `${source.src} ${source.width}w`).join(", ");
}

export function choosePrefetchSource(
  asset: AssetManifestEntry,
  viewportWidth: number,
  devicePixelRatio: number
): string | undefined {
  const sources = asset.sources.webp ?? asset.sources.avif;
  if (!sources || sources.length === 0) return undefined;
  const width = Number.isFinite(viewportWidth) ? Math.max(320, viewportWidth) : 320;
  const dpr = Number.isFinite(devicePixelRatio) ? Math.min(2, Math.max(1, devicePixelRatio)) : 1;
  const targetWidth = Math.min(1080, Math.max(480, Math.ceil(width * 0.75 * dpr)));
  return (sources.find((source) => source.width >= targetWidth) ?? sources.at(-1))?.src;
}

/** A single entry point for every generated image, including responsive sources and fallbacks. */
export function AssetImage({
  assetId,
  className,
  sizes = "100vw",
  alt,
  eager,
  draggable = false
}: AssetImageProps) {
  const asset = getAsset(assetId);
  const [settled, setSettled] = useState<{ assetId: AssetId; status: "loaded" | "error" } | null>(null);
  const loadStatus = settled?.assetId === assetId ? settled.status : "loading";
  const shouldEagerLoad = eager ?? asset.loading === "eager";
  const avifSet = sourceSet(asset, "avif");
  const webpSet = sourceSet(asset, "webp");

  const effectiveAlt = alt ?? asset.alt;

  return (
    <span
      className={`${className ? `${className} ` : ""}asset-picture asset-picture--${loadStatus}`}
      data-asset-id={assetId}
      data-load-status={loadStatus}
      aria-busy={loadStatus === "loading" ? "true" : undefined}
    >
      <picture>
        {avifSet && <source type="image/avif" srcSet={avifSet} sizes={sizes} />}
        {webpSet && <source type="image/webp" srcSet={webpSet} sizes={sizes} />}
        <img
          key={assetId}
          src={asset.fallbackSrc}
          alt={effectiveAlt}
          width={asset.width}
          height={asset.height}
          loading={shouldEagerLoad ? "eager" : "lazy"}
          fetchPriority={shouldEagerLoad ? "high" : "auto"}
          decoding="async"
          draggable={draggable}
          onLoad={() => setSettled({ assetId, status: "loaded" })}
          onError={() => setSettled({ assetId, status: "error" })}
        />
      </picture>
      {loadStatus === "error" && (effectiveAlt.trim() ? (
        <span className="asset-picture__error" role="status" aria-label={`图片加载失败：${effectiveAlt}`}>
          <AlertTriangle size={17} aria-hidden="true" />
          <span>图片加载失败</span>
        </span>
      ) : (
        <span className="asset-picture__error asset-picture__error--decorative" aria-hidden="true">
          <AlertTriangle size={17} /><span>图片加载失败</span>
        </span>
      ))}
    </span>
  );
}

/** Prefetches only the scene immediately ahead; it never fills the PWA cache with all source art. */
export function AssetPrefetch({ assetIds }: { assetIds: readonly AssetId[] }) {
  useEffect(() => {
    let cancelled = false;
    const schedule = window.requestIdleCallback ?? ((callback: IdleRequestCallback) => window.setTimeout(callback, 500));
    const cancel = window.cancelIdleCallback ?? window.clearTimeout;
    const handle = schedule(() => {
      if (cancelled) return;
      for (const assetId of assetIds) {
        const source = choosePrefetchSource(
          getAsset(assetId),
          document.documentElement.clientWidth || window.innerWidth,
          window.devicePixelRatio || 1
        );
        if (!source || document.head.querySelector(`link[data-prefetch-asset="${assetId}"]`)) continue;
        const link = document.createElement("link");
        link.rel = "prefetch";
        link.as = "image";
        link.href = source;
        link.dataset.prefetchAsset = assetId;
        document.head.append(link);
      }
    });
    return () => {
      cancelled = true;
      cancel(handle);
    };
  }, [assetIds]);

  return null;
}
